import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Save, Trash2, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useHubSpotFieldMappings, useSaveFieldMapping } from '@/hooks/useHubSpot';
import { toast } from 'sonner';

interface FieldMapping {
  id?: string;
  entity_type: string;
  hubspot_field: string;
  local_field: string;
  transformation_rule?: any;
  is_active: boolean;
  is_required?: boolean;
  default_value?: string;
}

const hubspotContactFields = [
  { value: 'email', label: 'Email' },
  { value: 'firstname', label: 'First Name' },
  { value: 'lastname', label: 'Last Name' },
  { value: 'phone', label: 'Phone' },
  { value: 'jobtitle', label: 'Job Title' },
  { value: 'company', label: 'Company' },
  { value: 'website', label: 'Website' },
  { value: 'lifecyclestage', label: 'Lifecycle Stage' },
];

const hubspotCompanyFields = [
  { value: 'name', label: 'Company Name' },
  { value: 'domain', label: 'Domain' },
  { value: 'industry', label: 'Industry' },
  { value: 'numberofemployees', label: 'Number of Employees' },
  { value: 'city', label: 'City' },
  { value: 'state', label: 'State/Region' },
  { value: 'country', label: 'Country' },
  { value: 'annualrevenue', label: 'Annual Revenue' },
];

const hubspotDealFields = [
  { value: 'dealname', label: 'Deal Name' },
  { value: 'amount', label: 'Amount' },
  { value: 'closedate', label: 'Close Date' },
  { value: 'dealstage', label: 'Deal Stage' },
  { value: 'pipeline', label: 'Pipeline' },
  { value: 'dealtype', label: 'Deal Type' },
];

const crmContactFields = [
  { value: 'email', label: 'Email' },
  { value: 'first_name', label: 'First Name' },
  { value: 'last_name', label: 'Last Name' },
  { value: 'phone', label: 'Phone' },
  { value: 'job_title', label: 'Job Title' },
  { value: 'organization_id', label: 'Organization' },
];

const crmCompanyFields = [
  { value: 'name', label: 'Organization Name' },
  { value: 'website', label: 'Website' },
  { value: 'industry', label: 'Industry' },
  { value: 'employee_count', label: 'Employee Count' },
  { value: 'city', label: 'City' },
  { value: 'state', label: 'State' },
  { value: 'country', label: 'Country' },
  { value: 'annual_revenue', label: 'Annual Revenue' },
];

const crmDealFields = [
  { value: 'name', label: 'Opportunity Name' },
  { value: 'amount', label: 'Amount' },
  { value: 'close_date', label: 'Close Date' },
  { value: 'stage', label: 'Stage' },
  { value: 'probability', label: 'Probability' },
];

export default function HubSpotMapping() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('contact');
  const { data: existingMappings } = useHubSpotFieldMappings(activeTab);
  const { mutate: saveMapping } = useSaveFieldMapping();

  const [mappings, setMappings] = useState<Record<string, FieldMapping[]>>({
    contact: existingMappings?.filter(m => m.entity_type === 'contact') || [],
    company: existingMappings?.filter(m => m.entity_type === 'company') || [],
    deal: existingMappings?.filter(m => m.entity_type === 'deal') || [],
  });

  const getHubspotFields = (entityType: string) => {
    switch (entityType) {
      case 'contact':
        return hubspotContactFields;
      case 'company':
        return hubspotCompanyFields;
      case 'deal':
        return hubspotDealFields;
      default:
        return [];
    }
  };

  const getCrmFields = (entityType: string) => {
    switch (entityType) {
      case 'contact':
        return crmContactFields;
      case 'company':
        return crmCompanyFields;
      case 'deal':
        return crmDealFields;
      default:
        return [];
    }
  };

  const addMapping = (entityType: string) => {
    const newMapping: FieldMapping = {
      entity_type: entityType,
      hubspot_field: '',
      local_field: '',
      is_active: true,
    };

    setMappings(prev => ({
      ...prev,
      [entityType]: [...prev[entityType], newMapping],
    }));
  };

  const updateMapping = (entityType: string, index: number, field: Partial<FieldMapping>) => {
    setMappings(prev => ({
      ...prev,
      [entityType]: prev[entityType].map((m, i) => 
        i === index ? { ...m, ...field } : m
      ),
    }));
  };

  const removeMapping = (entityType: string, index: number) => {
    setMappings(prev => ({
      ...prev,
      [entityType]: prev[entityType].filter((_, i) => i !== index),
    }));
  };

  const saveMappings = () => {
    const allMappings = Object.values(mappings).flat();
    
    allMappings.forEach(mapping => {
      if (mapping.hubspot_field && mapping.local_field) {
        saveMapping(mapping);
      }
    });
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/integrations/hubspot')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Field Mapping</h1>
            <p className="text-muted-foreground mt-1">
              Map HubSpot fields to CRM fields
            </p>
          </div>
        </div>
        <Button onClick={saveMappings}>
          <Save className="h-4 w-4 mr-2" />
          Save All Mappings
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="contact">Contacts</TabsTrigger>
          <TabsTrigger value="company">Companies</TabsTrigger>
          <TabsTrigger value="deal">Deals</TabsTrigger>
        </TabsList>

        {['contact', 'company', 'deal'].map((entityType) => (
          <TabsContent key={entityType} value={entityType} className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="capitalize">{entityType} Field Mappings</CardTitle>
                    <CardDescription>
                      Map HubSpot {entityType} fields to CRM fields
                    </CardDescription>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => addMapping(entityType)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Mapping
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Header */}
                  <div className="grid grid-cols-12 gap-4 text-sm font-medium text-muted-foreground">
                    <div className="col-span-4">HubSpot Field</div>
                    <div className="col-span-1 text-center"></div>
                    <div className="col-span-4">CRM Field</div>
                    <div className="col-span-2 text-center">Status</div>
                    <div className="col-span-1"></div>
                  </div>

                  {/* Mappings */}
                  {mappings[entityType].length > 0 ? (
                    mappings[entityType].map((mapping, index) => (
                      <div key={index} className="grid grid-cols-12 gap-4 items-center">
                        <div className="col-span-4">
                          <Select
                            value={mapping.hubspot_field}
                            onValueChange={(value) => 
                              updateMapping(entityType, index, { hubspot_field: value })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select HubSpot field" />
                            </SelectTrigger>
                            <SelectContent>
                              {getHubspotFields(entityType).map(field => (
                                <SelectItem key={field.value} value={field.value}>
                                  {field.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="col-span-1 flex justify-center">
                          <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        </div>

                        <div className="col-span-4">
                          <Select
                            value={mapping.local_field}
                            onValueChange={(value) => 
                              updateMapping(entityType, index, { local_field: value })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select CRM field" />
                            </SelectTrigger>
                            <SelectContent>
                              {getCrmFields(entityType).map(field => (
                                <SelectItem key={field.value} value={field.value}>
                                  {field.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="col-span-2 flex justify-center">
                          <Switch
                            checked={mapping.is_active}
                            onCheckedChange={(checked) => 
                              updateMapping(entityType, index, { is_active: checked })
                            }
                          />
                        </div>

                        <div className="col-span-1 flex justify-end">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeMapping(entityType, index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      No mappings configured. Click "Add Mapping" to get started.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Default Mappings Info */}
            <Card>
              <CardHeader>
                <CardTitle>Default Mappings</CardTitle>
                <CardDescription>
                  These fields are automatically mapped if not configured above
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {entityType === 'contact' && (
                    <>
                      <div className="flex items-center gap-2 text-sm">
                        <Badge variant="outline">email</Badge>
                        <ArrowRight className="h-3 w-3" />
                        <Badge variant="outline">email</Badge>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Badge variant="outline">firstname</Badge>
                        <ArrowRight className="h-3 w-3" />
                        <Badge variant="outline">first_name</Badge>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Badge variant="outline">lastname</Badge>
                        <ArrowRight className="h-3 w-3" />
                        <Badge variant="outline">last_name</Badge>
                      </div>
                    </>
                  )}
                  {entityType === 'company' && (
                    <>
                      <div className="flex items-center gap-2 text-sm">
                        <Badge variant="outline">name</Badge>
                        <ArrowRight className="h-3 w-3" />
                        <Badge variant="outline">name</Badge>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Badge variant="outline">domain</Badge>
                        <ArrowRight className="h-3 w-3" />
                        <Badge variant="outline">website</Badge>
                      </div>
                    </>
                  )}
                  {entityType === 'deal' && (
                    <>
                      <div className="flex items-center gap-2 text-sm">
                        <Badge variant="outline">dealname</Badge>
                        <ArrowRight className="h-3 w-3" />
                        <Badge variant="outline">name</Badge>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Badge variant="outline">amount</Badge>
                        <ArrowRight className="h-3 w-3" />
                        <Badge variant="outline">amount</Badge>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}